import { updateBoxDimensionsV2 } from "@/api/fulfillment";
import Button from "@/components/forms/Button/Button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { BoxTemplate, FbaBox, useShipmentContext } from "@/contexts/ShipmentContext";
import { useEffect, useState } from "react";
import { DimensionsAndWeight, getDefaultDimensionsAndWeight } from "../utils";
import DimensionsAndWeightRow, { boxToDimensionsAndWeight } from "../DimensionsAndWeightRow/DimensionsAndWeightRow";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { LocalStorageKeys, setLocalStorageItem } from "@/utils/localStorageUtils";
import styles from "./FbaUpdateBoxDimensionsModal.module.css";
import TextInput from "@/components/forms/TextInput/TextInput";
import Divider from '@/components/Divider/Divider';
import IconArrow, { Direction } from "@/components/icons/IconArrow";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import RadioInput from "@/components/forms/RadioInput";
import IconBox from "@/components/icons/IconBox";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { API, API2 } from "@/lib/api";

import {
  ShipmentUpdateBoxDimensionsBody,
} from "@boxem/core-api-schema";
import {
  RequestDTOBoxTemplateNew,
  ResponseDTOBoxTemplateState,
  RequestDTOBoxTemplateSetValues,
  RequestDTOBoxTemplateMakeIDDefault,
  RequestDTOBoxTemplateMakeValueDefault,
} from "@boxem/core-types";

export interface FbaUpdateBoxDimensionsModalProps {
  box?: FbaBox;
  onClose: () => void;
}

export default function FbaUpdateBoxDimensionsModal(props: FbaUpdateBoxDimensionsModalProps) {
  const { currentBatch, setCurrentBatch, boxTemplates, setBoxTemplatesDTO, setBoxTemplatesValue } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { box, onClose } = props;
  const [viewingTemplates, setViewingTemplates] = useState<boolean>(true);
  const [savingTemplateName, setSavingTemplateName] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [desiredDimensionsAndWeight, setDesiredDimensionsAndWeight] = useState<DimensionsAndWeight>(box ? boxToDimensionsAndWeight(box) : getDefaultDimensionsAndWeight(boxTemplates, setBoxTemplatesDTO));
  const [savingTemplate, setSavingTemplate] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string|null>(null);

  const [ applyChangesToBoxTemplates, setApplyChangesToBoxTemplates ] = useState<{[key: string]: DimensionsAndWeight}>({});
  function localTemplate(box: BoxTemplate): BoxTemplate {
    let result = {...box};
    const changes = applyChangesToBoxTemplates[box.id];
    if(changes) {
      result.length = changes.length;
      result.width = changes.width;
      result.height = changes.height;
      result.weight = changes.weight;
    }
    return result;
  }

  useEffect(() => {
    if (!!box) {
      const dimensions = boxToDimensionsAndWeight(box);
      desiredDimensionsAndWeight.length = dimensions.length;
      desiredDimensionsAndWeight.width = dimensions.width;
      desiredDimensionsAndWeight.height = dimensions.height;
      desiredDimensionsAndWeight.weight = dimensions.weight;
      // if (dimensions.height && dimensions.width && dimensions.length && dimensions.weight)
      setDesiredDimensionsAndWeight({...desiredDimensionsAndWeight});
    }
  }, [box]);

  const defaultTemplateRef: BoxTemplate|null = boxTemplates.templates.find(test => test.id === boxTemplates.defaultBoxTemplateId);
  const defaultTemplate: BoxTemplate|null = defaultTemplateRef ? localTemplate(defaultTemplateRef) : null;
  let isDefault = 
     defaultTemplate
  && defaultTemplate.isAutoGenerated
  && defaultTemplate.width == box?.width
  && defaultTemplate.height == box?.height
  && defaultTemplate.length == box?.length
  && defaultTemplate.weight == box?.weight;

  return (
    <Modal
      open={!!box}
      className={styles.modal}
      closeOnOutsideClick={false}
      style={{overflowY:"auto"}}
      onOpenChange={(open: boolean) => {
        if (!open) onClose();
      }}
    >
      <ModalHeader><h3>Update Box Dimensions & Weight</h3></ModalHeader>

      <Flex style={{overflowY: "auto"}} direction="column" align="stretch">
        <Flex className={styles.row} direction="row" justify="space-between">
          <p className="weak">Courier rates can depend on the boxes' dimensions and weight.</p>
          <Button
            type="primary"
            disabled={savingTemplate}
            style={{alignSelf: "flex-end"}}
            onClick={()=> { setSavingTemplate(true); }}
          >
            Save as a template
          </Button>
        </Flex>
        <Flex gap={4} className={styles.row}>
          <DimensionsAndWeightRow
            oversizedControls
            showMaxWeightAndDimensionsTooltip
            value={desiredDimensionsAndWeight}
            onChange={(e) => {
              setDesiredDimensionsAndWeight({...e});
            }}
            isDefault={isDefault}
            makeDefault={() => {
              // @COPYPASTA(michaelp)(templanostuff):
              // @NOTE(michaelp): Predictive client side change based on server logic
              // so that 3G connections are happily snappy
              // We can make the ID whatever here, for auto-generated boxes
              // it's only used on the client to find what values it refers to.
              // (For example, the user can't see auto generated boxes, so they can't accidentally send requests to delete them.)
              boxTemplates.templates = boxTemplates.templates.filter(t => !t.isAutoGenerated);
              const temporaryBox: BoxTemplate = {
                id: Math.random().toString(),
                name: "Temporary client side default",
                length: box.length,
                width: box.width,
                height: box.height,
                weight: box.weight,
                isAutoGenerated: true,
              };
              boxTemplates.templates.push(temporaryBox);
              boxTemplates.defaultBoxTemplateId = temporaryBox.id;
              setBoxTemplatesValue(boxTemplates);
              const params: RequestDTOBoxTemplateMakeValueDefault = {
                length: box.length,
                width: box.width,
                height: box.height,
                weight: box.weight,
              };
              API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/makeDefaultByValue", params).then(e => {setBoxTemplatesDTO(e); console.log(e, "eeee"); })
                .catch(err => { console.log("Making default error", err); });
            }}
          />
        </Flex>
          {savingTemplate && <span className={styles.row} style={{paddingBottom:"6px"}}>Enter a name for your new saved box template.<span style={{color: "var(--sentiment-text-negative)"}}>*</span></span>}
          {savingTemplate && <Flex className={styles.row} style={{paddingTop: "0px"}} direction="row" gap={2} wrap="nowrap">
            <TextInput value={savingTemplateName} onChange={(val)=>{setSavingTemplateName(val);}} placeholder="Box Template Name" maxLength={32} />
            <Button type="secondary" onClick={()=>{ setSavingTemplate(false); }}>Cancel</Button>
            <Button type="primary" onClick={()=>{
              setSavingTemplate(false); 
              let params: RequestDTOBoxTemplateNew = {
                name: savingTemplateName,
                length: desiredDimensionsAndWeight.length,
                width: desiredDimensionsAndWeight.width,
                height: desiredDimensionsAndWeight.height,
                weight: desiredDimensionsAndWeight.weight,
              };
              setSavingTemplateName("");
              API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/new", params).then(setBoxTemplatesDTO)
              .catch(err => { console.log("NAY", err); });
            }}>Save</Button>
          </Flex>}

        <Divider margin = "0"/>
        <div className={`${styles.rowHeader} ${styles.row}`}>
          <Button className={styles.savedTemplatesButton} type="text" style={{height: "fit-content", minHeight: "unset", display: "flex", justifyContent: "space-between", width: "100%"}} onClick = {()=>{setViewingTemplates(!viewingTemplates);}}>
            <span style={{fontWeight: "900"}}>Saved Box Templates</span>
            <div className={styles.arrowButton} style={{marginRight: "24px"}}>
              <IconArrow size={24} direction={viewingTemplates ? Direction.Down : Direction.Up} style={{ transition: "transform 200ms" }} />
            </div>
          </Button>
        </div>
        {viewingTemplates && <div>
          <div style={{paddingTop: "0"}} className={styles.row}>Select a box template to apply it to the current box.</div>
        </div>}
        
        {viewingTemplates && boxTemplates.templates.filter(t => !t.isAutoGenerated).map((templateRef, index) => {
          const template = localTemplate(templateRef);
          const checked = template.id === selectedTemplate;
          return (
            <Flex className={`${styles.row} ${checked ? styles.checkedRadioBox : ""} ${styles.radioBox}`} key={"templateSelect" + template.id} align="center" gap={2}>
              <RadioInput
                value={template.id}
                allowDeselectNull={true}
                checked={checked}
                onChange={(value: string) => { setSelectedTemplate(value);}}
              />
              <IconBox />
              {/*@COPYPASTA(michaelp)(asbestosisthebestos): Search tag 'asbestosisthebestos' in codebase to find duplicated code*/}
              <Tooltip delayMs={0} placement="left" title={template.name}><h3 style={{whiteSpace: "nowrap", overflow: "hidden", width: "16rem", minWidth: "1.5rem", textOverflow: "ellipsis"}}>{template.name}</h3></Tooltip>
              <DimensionsAndWeightRow
                showLabels={false}
                oversizedTooltip={false}
                value={{ weight: template.weight, width: template.width, length: template.length, height: template.height }}
                isDefault={ boxTemplates.defaultBoxTemplateId == template.id }
                makeDefault={() => {
                  boxTemplates.defaultBoxTemplateId = template.id;
                  setBoxTemplatesValue(boxTemplates);
                  const params: RequestDTOBoxTemplateMakeIDDefault = { templateId: template.id };
                  API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/makeDefault", params).then(setBoxTemplatesDTO)
                    .catch(err => { console.log("Makign template id default error, ", err); });
                }}
                onChange={(e) => {
                  let newApply = applyChangesToBoxTemplates;
                  newApply[template.id] = {
                    length: e.length,
                    width: e.width,
                    height: e.height,
                    weight: e.weight,
                  };
                  setApplyChangesToBoxTemplates({...newApply});
                }}
              />
            </Flex>
          );
        })}
      </Flex>

      <ModalFooter>
        <Flex
          justify="end"
          gap={2}
        >
          {loading 
            ? <LoadingSpinner /> 
            : <>
              <Button
                size="large"
                type="secondary"
                onClick={() => onClose()}
              >
                Cancel
              </Button>
              <Button
                size="large"
                type="primary"
                onClick={() => {
                  setLoading(true);

                  let setAnyTemplates = false;
                  for(const srcId in applyChangesToBoxTemplates) {
                    const src: DimensionsAndWeight = applyChangesToBoxTemplates[srcId];
                    const dest: BoxTemplate = boxTemplates.templates.find(t => t.id?.toString() === srcId);
                    if(dest.length != src.length
                    || dest.width != src.width
                    || dest.height != src.height
                    || dest.weight != src.weight) {
                      dest.length = src.length;
                      dest.width = src.width;
                      dest.height = src.height;
                      dest.weight = src.weight;
                      setAnyTemplates = true;
                    }
                  }
                  if(setAnyTemplates) {
                    const params: RequestDTOBoxTemplateSetValues = [];
                    for(const tRef of boxTemplates.templates) {
                      const t = localTemplate(tRef);
                      params.push({
                        id: t.id,
                        length: t.length,
                        width: t.width,
                        height: t.height,
                        weight: t.weight,
                      });
                    }
                    console.log("Bulk updating them !", params);
                    API2.post<ResponseDTOBoxTemplateState>("/boxTemplates/setValues", params).then(setBoxTemplatesDTO)
                      .catch(err => { console.log("R N R ", err); });
                  }

                  const dto: ShipmentUpdateBoxDimensionsBody = {
                    length: desiredDimensionsAndWeight.length,
                    width: desiredDimensionsAndWeight.width,
                    height: desiredDimensionsAndWeight.height,
                    weight: desiredDimensionsAndWeight.weight,
                  };
                  const templateToApplyRef = boxTemplates.templates.find(t => t.id === selectedTemplate);
                  if(templateToApplyRef) {
                    const templateToApply = localTemplate(templateToApplyRef);
                    dto.length = templateToApply.length;
                    dto.width = templateToApply.width;
                    dto.height = templateToApply.height;
                    dto.weight = templateToApply.weight;
                  }
                  setSelectedTemplate("");
                  const undoDTO: ShipmentUpdateBoxDimensionsBody = {
                    length: box.length,
                    width: box.width,
                    height: box.height,
                    weight: box.weight,
                  };
                  Object.assign(box, dto);
                  setCurrentBatch({ ...currentBatch });   
                  updateBoxDimensionsV2(box.id, dto)
                    .catch(errors => {
                      Object.assign(box, undoDTO);
                      setCurrentBatch({ ...currentBatch });   
                      handleErrorResponseGenerically(errors);
                    })
                    .finally(() => {
                      setLoading(false);
                      onClose?.();
                    });
                }}
              >
                Update
              </Button>
            </>
          }
        </Flex>
      </ModalFooter>
    </Modal>
  );
}